import { Button, Dropdown, FormInstance, Tabs } from 'antd';
import useModal from 'antd/es/modal/useModal';
import { Table } from 'antd/lib';
import { EnterpriseOrderFormComponent } from './enterprise-order-form.component';
import { useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  useCreateEnterpriseOrder,
  useDeleteEnterpriseOrder,
  useEnterpriseOrders,
  useExportEnterpriseStatement,
  useExportProviderStatement,
  useUpdateEnterpriseOrder,
} from '../utils/api-hooks/enterprise-order.hook';
import { usePagination } from '../shared/usePagination';
import { map, round, sumBy } from 'lodash';
import {
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ExportOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { GetEnterpriseOrdersResult } from '../utils/api-client';
import { EnterpriseOrderEditFormComponent } from './enterprise-order-edit-form.component';
import { NavLink } from 'react-router-dom';
import {
  ENTERPRISE_ORDER_STATUS,
  ENTERPRISE_ORDER_STATUS_LABEL,
} from '../constants/enterprise-order';

export const EnterpriseOrdersContainer: React.FC = () => {
  const [modal, contextHolder] = useModal();
  const [status, setStatus] = useState<ENTERPRISE_ORDER_STATUS>(
    ENTERPRISE_ORDER_STATUS.ONGOING
  );
  const formRef = useRef<
    FormInstance<{
      clientName: string;
      clientTaxId: string;
      contactInfo: string;
      deliveryAddress: string;
      deliveryDate: Dayjs;
      deliveryFee: number;
      discount: number;
      items: {
        providerId: string;
        name: string;
        quantity: number;
        price: number;
      }[];
    }>
  >(null);
  const pagination = usePagination();
  const { mutateAsync: exportEnterpriseStatement, isLoading: isExporting } =
    useExportEnterpriseStatement();
  const {
    mutateAsync: exportProviderStatement,
    isLoading: isExportingProviderStatement,
  } = useExportProviderStatement();
  const { mutateAsync: createEnterpriseOrder, isLoading: isCreating } =
    useCreateEnterpriseOrder();
  const { mutateAsync: updateEnterpriseOrder, isLoading: isUpdating } =
    useUpdateEnterpriseOrder();
  const { mutateAsync: deleteEnterpriseOrder, isLoading: isDeleting } =
    useDeleteEnterpriseOrder();
  const {
    data: { data, total } = {},
    isLoading,
    isFetching,
  } = useEnterpriseOrders({
    params: [pagination.skip, pagination.take, [status]],
  });

  const onEditButtonClick = (
    record: GetEnterpriseOrdersResult['data'][number]
  ) => {
    modal.confirm({
      title: '編輯訂單',
      icon: null,
      width: 640,
      content: (
        <EnterpriseOrderEditFormComponent formRef={formRef} data={record} />
      ),
      async onOk() {
        const values = await formRef.current!.validateFields();

        return updateEnterpriseOrder({
          ...values,
          id: record.id,
          deliveryDate: values.deliveryDate.toISOString(),
          items: map(values.items, (item) => ({
            ...item,
          })),
        });
      },
    });
  };

  const onCompleteButtonClick = (
    record: GetEnterpriseOrdersResult['data'][number]
  ) => {
    modal.confirm({
      title: '完成訂單',
      content: '確定要將這筆訂單的狀態切換成已完成這嗎？',
      async onOk() {
        await updateEnterpriseOrder({
          ...record,
          status: ENTERPRISE_ORDER_STATUS.COMPLETED,
        });
      },
    });
  };

  const onCreateBtnClick = () => {
    modal.confirm({
      title: '新增訂單',
      icon: null,
      width: 640,
      content: <EnterpriseOrderFormComponent formRef={formRef} />,
      async onOk() {
        const values = await formRef.current!.validateFields();

        return createEnterpriseOrder({
          ...values,
          deliveryDate: values.deliveryDate.toISOString(),
          items: map(values.items, (item) => ({
            ...item,
          })),
        });
      },
    });
  };

  const onDeleteButtonClick = (
    record: GetEnterpriseOrdersResult['data'][number]
  ) => {
    modal.confirm({
      title: '刪除訂單',
      content: '確定要刪除這筆訂單嗎？',
      async onOk() {
        await deleteEnterpriseOrder(record.id);
      },
    });
  };

  return (
    <div className="flex flex-col items-stretch gap-4">
      {contextHolder}
      <span className="text-2xl font-bold">企業訂單</span>
      <div>
        <Tabs
          defaultActiveKey={status}
          onChange={(key: string) => {
            setStatus(key as ENTERPRISE_ORDER_STATUS);
          }}
          items={[
            {
              key: ENTERPRISE_ORDER_STATUS.ONGOING,
              label:
                ENTERPRISE_ORDER_STATUS_LABEL[ENTERPRISE_ORDER_STATUS.ONGOING],
            },
            {
              key: ENTERPRISE_ORDER_STATUS.COMPLETED,
              label:
                ENTERPRISE_ORDER_STATUS_LABEL[
                  ENTERPRISE_ORDER_STATUS.COMPLETED
                ],
            },
          ]}
        />
        <Table
          rowKey="id"
          size="small"
          loading={
            isLoading ||
            isCreating ||
            isUpdating ||
            isFetching ||
            isDeleting ||
            isExporting ||
            isExportingProviderStatement
          }
          dataSource={data}
          scroll={{ x: 'max-content' }}
          pagination={{
            ...pagination,
            total,
          }}
          columns={[
            {
              align: 'center',
              title: '狀態',
              width: 60,
              dataIndex: 'status',
              render: (text) => {
                return ENTERPRISE_ORDER_STATUS_LABEL[
                  text as keyof typeof ENTERPRISE_ORDER_STATUS_LABEL
                ];
              },
            },
            {
              align: 'center',
              title: '訂餐日期',
              width: 150,
              dataIndex: 'deliveryDate',
              render: (text) => {
                return dayjs(text).format('YYYY/MM/DD HH:mm');
              },
            },
            {
              title: '客戶資訊',
              dataIndex: 'clientName',
              render: (text, record) => {
                return (
                  <div>
                    <div>客戶名稱：{text}</div>
                    <div>統一編號：{record.clientTaxId}</div>
                    <div>聯絡資訊：{record.contactInfo}</div>
                    <div>送貨地址：{record.deliveryAddress}</div>
                  </div>
                );
              },
            },
            {
              title: '品項',
              dataIndex: 'items',
              render: (items, record) => {
                return (
                  <div>
                    {map(record.items, (item) => {
                      return (
                        <div key={item.id}>
                          (
                          <NavLink
                            to={`/providers/${item.providerId}/business-info`}
                            target="_blank"
                          >
                            {item.providerIdNumber} - {item.providerName}
                          </NavLink>
                          ) - {item.name}(
                          {item.price.toLocaleString('zh-TW', {
                            style: 'currency',
                            currency: 'TWD',
                            minimumFractionDigits: 0,
                          })}
                          ) x {item.quantity}
                        </div>
                      );
                    })}
                  </div>
                );
              },
            },
            {
              align: 'center',
              title: '品項總額',
              dataIndex: 'items',
              render: (items, record) => {
                const totalPrice = sumBy(record.items, (item) => {
                  return item.price * item.quantity;
                });

                return totalPrice.toLocaleString('zh-TW', {
                  style: 'currency',
                  currency: 'TWD',
                  minimumFractionDigits: 0,
                });
              },
            },
            {
              align: 'center',
              title: '運費',
              dataIndex: 'deliveryFee',
              render: (text) => {
                return text.toLocaleString('zh-TW', {
                  style: 'currency',
                  currency: 'TWD',
                  minimumFractionDigits: 0,
                });
              },
            },
            {
              align: 'center',
              title: '折扣',
              dataIndex: 'discount',
              render: (text, record) => {
                const totalPrice = sumBy(record.items, (item) => {
                  return item.price * item.quantity;
                });
                const discount = round(totalPrice * (text / 100));

                return (
                  text +
                  '%' +
                  '(' +
                  discount.toLocaleString('zh-TW', {
                    style: 'currency',
                    currency: 'TWD',
                    minimumFractionDigits: 0,
                  }) +
                  ')'
                );
              },
            },
            {
              align: 'center',
              title: '總金額',
              dataIndex: 'totalPrice',
              render: (text, record) => {
                const totalPrice = sumBy(record.items, (item) => {
                  return item.price * item.quantity;
                });
                const discount = round(totalPrice * (record.discount / 100));

                return (
                  totalPrice -
                  discount +
                  record.deliveryFee
                ).toLocaleString('zh-TW', {
                  style: 'currency',
                  currency: 'TWD',
                  minimumFractionDigits: 0,
                });
              },
            },
            {
              align: 'center',
              title: '負責人',
              width: 100,
              dataIndex: 'creatorName',
            },
            ...(status === ENTERPRISE_ORDER_STATUS.ONGOING
              ? [
                  {
                    title: '操作',
                    width: 50,
                    render: (text: any, record: any) => {
                      return (
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: 'edit',
                                label: '編輯訂單',
                                icon: <EditOutlined />,
                                onClick: () => {
                                  onEditButtonClick(record);
                                },
                              },
                              {
                                key: 'complete',
                                label: '完成訂單',
                                icon: <CheckOutlined />,
                                onClick: () => {
                                  onCompleteButtonClick(record);
                                },
                              },
                              {
                                key: 'divider',
                                type: 'divider',
                              },
                              {
                                key: 'export-enterprise',
                                label: '匯出企業對帳單',
                                icon: <ExportOutlined />,
                                onClick: () => {
                                  exportEnterpriseStatement({
                                    id: record.id,
                                    name: record.clientName,
                                    date: dayjs(record.deliveryDate).format(
                                      'YYYY/MM/DD'
                                    ),
                                  });
                                },
                              },
                              {
                                key: 'export-provider',
                                label: '匯出供應商對帳單',
                                icon: <ExportOutlined />,
                                onClick: () => {
                                  exportProviderStatement({
                                    id: record.id,
                                    name: record.clientName,
                                    date: dayjs(record.deliveryDate).format(
                                      'YYYY/MM/DD'
                                    ),
                                  });
                                },
                              },
                              {
                                key: 'divider1',
                                type: 'divider',
                              },
                              {
                                key: 'delete',
                                label: '刪除訂單',
                                danger: true,
                                icon: <DeleteOutlined />,
                                onClick: () => {
                                  onDeleteButtonClick(record);
                                },
                              },
                            ],
                          }}
                        >
                          <Button
                            icon={<MoreOutlined />}
                            shape="circle"
                            size="small"
                          />
                        </Dropdown>
                      );
                    },
                  },
                ]
              : []),
          ]}
          title={
            status === ENTERPRISE_ORDER_STATUS.ONGOING
              ? () => {
                  return (
                    <Button type="primary" onClick={onCreateBtnClick}>
                      新增訂單
                    </Button>
                  );
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};
